import React, { forwardRef, useImperativeHandle, useState } from "react";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { Box, Checkbox, FormControlLabel, Grid, TextField, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import * as Yup from "yup";
import { CardBrandIcons } from "./CardBrandIcons";

const STRIPE_ELEMENT_STYLE = {
  style: {
    base: {
      fontSize: "16px",
      color: "#424770",
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      "::placeholder": { color: "#aab7c4" },
    },
    invalid: { color: "#e53935", iconColor: "#e53935" },
  },
};

const stripeFieldFocusSx = {
  "&:focus-within": {
    borderColor: "primary.main",
    borderWidth: 2,
    p: "11px",
    minHeight: 48,
  },
};

/** Full-width container so Stripe iframes receive a non-zero width (required for expiry/CVC). */
const stripeInputBoxSx = {
  border: "1px solid",
  borderColor: "grey.400",
  borderRadius: 1,
  p: 1.5,
  minHeight: 48,
  width: "100%",
  boxSizing: "border-box",
  bgcolor: "background.paper",
  position: "relative",
  zIndex: 1,
  ...stripeFieldFocusSx,
};

const stripeElementHostSx = {
  width: "100%",
  minHeight: 24,
  lineHeight: 0,
  "& .StripeElement": {
    width: "100%",
  },
};

const stripeNumberBoxSx = {
  ...stripeInputBoxSx,
  display: "flex",
  alignItems: "center",
  gap: 1,
  pr: 1.5,
};

const fieldCaptionSx = { mb: 0.5, display: "block", minHeight: 18 };

export type OneOffPaymentMethodResult = {
  paymentMethodId: string;
  cardholderName: string;
  brand: string;
  last4: string;
};

export type CheckoutOneOffCardFormHandle = {
  getSaveCard: () => boolean;
  createOneOffPaymentMethod: () => Promise<OneOffPaymentMethodResult>;
};

type CheckoutOneOffCardFormValues = {
  name: string;
  postalCode: string;
};

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Name on card is required"),
  postalCode: Yup.string()
    .required("ZIP code is required")
    .matches(/^\d{5}(-\d{4})?$/, "Enter a valid US ZIP code"),
});

type CheckoutOneOffCardFormProps = {
  defaultEmail?: string;
};

export const CheckoutOneOffCardForm = forwardRef<
  CheckoutOneOffCardFormHandle,
  CheckoutOneOffCardFormProps
>(function CheckoutOneOffCardForm({ defaultEmail }, ref) {
  const stripe = useStripe();
  const elements = useElements();
  const [saveCard, setSaveCard] = useState(false);

  const {
    register,
    getValues,
    formState: { errors },
  } = useForm<CheckoutOneOffCardFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: { name: "", postalCode: "" },
  });

  useImperativeHandle(ref, () => ({
    getSaveCard: () => saveCard,
    createOneOffPaymentMethod: async () => {
      if (!stripe || !elements) {
        throw new Error("Card form is not ready yet. Please wait a moment.");
      }

      const name = getValues("name")?.trim();
      const postalCode = getValues("postalCode")?.trim();
      if (!name) {
        throw new Error("Name on card is required.");
      }
      if (!postalCode) {
        throw new Error("ZIP code is required.");
      }

      const cardNumberElement = elements.getElement(CardNumberElement);
      if (!cardNumberElement) {
        throw new Error("Card form is not ready yet.");
      }

      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: "card",
        card: cardNumberElement,
        billing_details: {
          name,
          email: defaultEmail || undefined,
          address: {
            postal_code: postalCode,
            country: "US",
          },
        },
      });

      if (error) {
        throw new Error(error.message || "Card was not accepted.");
      }

      if (paymentMethod?.card?.country && paymentMethod.card.country !== "US") {
        throw new Error("Only US-issued cards are accepted.");
      }

      if (!paymentMethod?.id) {
        throw new Error("Could not process card. Please try again.");
      }

      const brand = paymentMethod.card?.brand || "card";
      return {
        paymentMethodId: paymentMethod.id,
        cardholderName: name,
        brand,
        last4: paymentMethod.card?.last4 || "",
      };
    },
  }));

  return (
    <Box sx={{ mt: 0.5 }}>
      <Grid container spacing={1.5}>
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary" sx={fieldCaptionSx}>
            Name on card
          </Typography>
          <Box
            sx={{
              ...stripeInputBoxSx,
              display: "flex",
              alignItems: "center",
              borderColor: errors.name ? "error.main" : "grey.400",
            }}
          >
            <TextField
              size="small"
              fullWidth
              required
              placeholder="Name on card"
              variant="standard"
              InputProps={{ disableUnderline: true }}
              inputProps={{ autoComplete: "cc-name" }}
              sx={{
                m: 0,
                "& .MuiInputBase-root": { fontSize: 16, py: 0 },
                "& .MuiInputBase-input": { p: 0 },
              }}
              {...register("name")}
              error={!!errors.name}
            />
          </Box>
          {errors.name && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
              {errors.name.message?.toString()}
            </Typography>
          )}
        </Grid>
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary" sx={fieldCaptionSx}>
            Card number
          </Typography>
          <Box sx={stripeNumberBoxSx}>
            <Box sx={{ ...stripeElementHostSx, flex: 1, minWidth: 0 }}>
              <CardNumberElement
                options={{
                  ...STRIPE_ELEMENT_STYLE,
                  showIcon: false,
                  placeholder: "Card number",
                }}
              />
            </Box>
            <CardBrandIcons compact />
          </Box>
        </Grid>
        <Grid item xs={12} sm={4} sx={{ display: "flex", flexDirection: "column" }}>
          <Typography variant="caption" color="text.secondary" sx={fieldCaptionSx}>
            Expiration date
          </Typography>
          <Box sx={stripeInputBoxSx}>
            <Box sx={stripeElementHostSx}>
              <CardExpiryElement
                options={{
                  ...STRIPE_ELEMENT_STYLE,
                  placeholder: "MM / YY",
                }}
              />
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} sm={4} sx={{ display: "flex", flexDirection: "column" }}>
          <Typography variant="caption" color="text.secondary" sx={fieldCaptionSx}>
            Security code
          </Typography>
          <Box sx={stripeInputBoxSx}>
            <Box sx={stripeElementHostSx}>
              <CardCvcElement
                options={{
                  ...STRIPE_ELEMENT_STYLE,
                  placeholder: "CVC",
                }}
              />
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} sm={4} sx={{ display: "flex", flexDirection: "column" }}>
          <Typography variant="caption" color="text.secondary" sx={fieldCaptionSx}>
            ZIP code
          </Typography>
          <Box
            sx={{
              ...stripeInputBoxSx,
              display: "flex",
              alignItems: "center",
              borderColor: errors.postalCode ? "error.main" : "grey.400",
            }}
          >
            <TextField
              size="small"
              fullWidth
              required
              placeholder="12345"
              variant="standard"
              InputProps={{ disableUnderline: true }}
              inputProps={{ inputMode: "numeric", autoComplete: "postal-code" }}
              sx={{
                m: 0,
                "& .MuiInputBase-root": { fontSize: 16, py: 0 },
                "& .MuiInputBase-input": { p: 0 },
              }}
              {...register("postalCode")}
              error={!!errors.postalCode}
            />
          </Box>
          {errors.postalCode && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
              {errors.postalCode.message?.toString()}
            </Typography>
          )}
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={saveCard}
                onChange={(e) => setSaveCard(e.target.checked)}
                size="small"
              />
            }
            label={<Typography variant="body2">Save this card</Typography>}
            sx={{ m: 0 }}
          />
        </Grid>
      </Grid>
    </Box>
  );
});

export default CheckoutOneOffCardForm;
